import { company } from '../data/site'

/** Navy og varm off-white fra LYKKEVÆRK-paletten. */
const THEME_COLOR = '#16232F'
const BACKGROUND_COLOR = '#F5F4F1'

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * Tegner logomærket centreret på en navy flade som kvadratisk PNG.
 * Falder tilbage til selve logo-filen, hvis billedet ikke kan tegnes.
 */
function renderIcon(image: HTMLImageElement | null, size: number, padding: number): string {
  if (!image) return company.logoMark
  try {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return company.logoMark
    ctx.fillStyle = THEME_COLOR
    ctx.fillRect(0, 0, size, size)
    const inner = size * (1 - padding * 2)
    const scale = Math.min(inner / image.naturalWidth, inner / image.naturalHeight)
    const width = image.naturalWidth * scale
    const height = image.naturalHeight * scale
    ctx.drawImage(image, (size - width) / 2, (size - height) / 2, width, height)
    return canvas.toDataURL('image/png')
  } catch {
    return company.logoMark
  }
}

function loadLogo(): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = company.logoMark
  })
}

function setMeta(name: string, content: string, created: Element[]) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
    created.push(tag)
  }
  tag.content = content
}

/**
 * Manifest og app-tags sættes KUN på admin-siderne, så den offentlige
 * hjemmeside er uændret. Returnerer en oprydningsfunktion.
 */
export function applyAdminAppMeta(): () => void {
  const created: Element[] = []
  let cancelled = false
  const touchIcon = document.head.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  const previousTouchIcon = touchIcon?.href ?? null

  setMeta('theme-color', THEME_COLOR, created)
  setMeta('apple-mobile-web-app-capable', 'yes', created)
  setMeta('mobile-web-app-capable', 'yes', created)
  setMeta('apple-mobile-web-app-title', 'LYKKEVÆRK', created)
  setMeta('apple-mobile-web-app-status-bar-style', 'black-translucent', created)

  void loadLogo().then((image) => {
    if (cancelled) return
    const origin = window.location.origin
    const manifest = {
      id: `${origin}/admin/`,
      name: 'LYKKEVÆRK Admin',
      short_name: 'LYKKEVÆRK',
      start_url: `${origin}/admin/i-dag`,
      scope: `${origin}/admin/`,
      display: 'standalone',
      orientation: 'portrait',
      theme_color: THEME_COLOR,
      background_color: BACKGROUND_COLOR,
      lang: 'da',
      icons: [
        { src: renderIcon(image, 192, 0.14), sizes: '192x192', type: 'image/png', purpose: 'any' },
        { src: renderIcon(image, 512, 0.14), sizes: '512x512', type: 'image/png', purpose: 'any' },
        { src: renderIcon(image, 512, 0.22), sizes: '512x512', type: 'image/png', purpose: 'maskable' },
      ],
    }
    const link = document.createElement('link')
    link.rel = 'manifest'
    link.href = `data:application/manifest+json;charset=utf-8,${encodeURIComponent(JSON.stringify(manifest))}`
    document.head.querySelector('link[rel="manifest"]')?.remove()
    document.head.appendChild(link)
    created.push(link)

    const appleIcon = renderIcon(image, 180, 0.14)
    if (touchIcon) {
      touchIcon.href = appleIcon
    } else {
      const icon = document.createElement('link')
      icon.rel = 'apple-touch-icon'
      icon.setAttribute('sizes', '180x180')
      icon.href = appleIcon
      document.head.appendChild(icon)
      created.push(icon)
    }
  })

  return () => {
    cancelled = true
    created.forEach((element) => element.remove())
    if (touchIcon && previousTouchIcon) touchIcon.href = previousTouchIcon
  }
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null

/**
 * Registrerer service workeren (kun admin). Den gemmer kun app-skallen —
 * aldrig Firestore-data, login-oplysninger eller kundebilleder.
 * Kan filen ikke serveres fra roden af domænet, fortsætter appen uden.
 */
export function registerAdminServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration
  registration = (async () => {
    if (!('serviceWorker' in navigator) || !window.isSecureContext) return null
    try {
      return await navigator.serviceWorker.register('/sw.js', { scope: '/admin/' })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.info('[PWA] service worker kunne ikke registreres', error)
      return null
    }
  })()
  return registration
}
